import { setup, assign, assertEvent } from "xstate";
import type { models } from "jiffy-api";
import type { printOnDemand } from "common/utils/models";
import type { UrlFlagsContext } from "@design-lab/widgets/design-lab-upload-artwork/model/url-flags.machine";

export type InitStage =
  | "readingParams"
  | "loadingProject"
  | "creatingProject"
  | "loadingProducts"
  | "restoringData"
  | "processingWorkflows"
  | "ready"
  | "error";

export type WorkflowType =
  | "autoUpload"
  | "designLibrary"
  | "canva"
  | "aiDesign"
  | "pdpOnly"
  | "none";

export type DesignLabInitContext = {
  // URL parameters
  urlFlags: UrlFlagsContext | null;

  // Project data
  projectUuid: string | null;
  projectData: models.designLab.getLatestDesignProject.Response | null;
  isNewProject: boolean;

  // Products data
  catalogIds: string[];
  productsMap: Map<string, printOnDemand.getProduct.Composition>;
  allProductsLoaded: boolean;

  // Restoration state
  hasRestored: boolean;

  // Workflow processing
  activeWorkflow: WorkflowType;
  targetSide: "front" | "back";

  // Error state
  error: Error | null;
  errorStage: InitStage | null;

  // Flags
  isEditMode: boolean;
  isSample: boolean;
};

export const designLabInitMachine = setup({
  types: {
    context: {} as DesignLabInitContext,
    events: {} as
      | { type: "START_INIT"; urlFlags: UrlFlagsContext; isEditMode: boolean; isSample: boolean }
      | { type: "PARAMS_READY" }
      | { type: "PROJECT_LOADED"; data: models.designLab.getLatestDesignProject.Response }
      | { type: "PROJECT_NOT_FOUND" }
      | { type: "PROJECT_ERROR"; error: Error }
      | { type: "PROJECT_CREATED"; uuid: string }
      | { type: "PRODUCTS_LOADED"; productsMap: Map<string, printOnDemand.getProduct.Composition> }
      | { type: "PRODUCTS_ERROR"; error: Error }
      | { type: "DATA_RESTORED" }
      | { type: "RESTORATION_ERROR"; error: Error }
      | { type: "WORKFLOW_COMPLETE" }
      | { type: "WORKFLOW_ERROR"; error: Error }
      | { type: "LIBRARY_SELECTION"; artworkId: number }
      | { type: "LIBRARY_CANCELLED" }
      | { type: "CANVA_COMPLETE"; file: File }
      | { type: "CANVA_CANCELLED" }
      | { type: "UPLOAD_COMPLETE" }
      | { type: "AI_COMPLETE" }
      | { type: "RETRY_INIT" }
      | { type: "RESET" }
  },
  guards: {
    hasAutoUpload: ({ context }) => {
      const flags = context.urlFlags?.transientData;
      return Boolean(
        (flags?.artworkUrls || flags?.uploadUrls) &&
          flags?.fileContentType &&
          flags?.catalogNumber &&
          flags?.activeComposition
      );
    },

    hasDesignLibrary: ({ context }) => Boolean(context.urlFlags?.workflowFlags?.hasDesignLibrary),

    hasCanva: ({ context }) => Boolean(context.urlFlags?.workflowFlags?.hasCanva),

    hasAiDesign: ({ context }) => Boolean(context.urlFlags?.workflowFlags?.hasAiDesign),

    hasPdpOnly: ({ context }) => {
      const flags = context.urlFlags?.transientData;
      return Boolean(
        flags?.catalogNumber &&
          flags?.activeComposition &&
          flags?.pdpQuantities &&
          flags?.pdpSizeData &&
          !context.urlFlags?.workflowFlags?.hasDesignLibrary &&
          !context.urlFlags?.workflowFlags?.hasCanva &&
          !context.urlFlags?.workflowFlags?.hasAiDesign
      );
    },

    hasWorkflow: ({ context }) => context.activeWorkflow !== "none",

    shouldCreateProject: ({ context }) => !context.projectUuid && context.isNewProject
  },
  actions: {
    setInitialContext: assign(({ event }) => {
      assertEvent(event, "START_INIT");
      return {
        urlFlags: event.urlFlags,
        isEditMode: event.isEditMode,
        isSample: event.isSample,
        projectUuid: null,
        projectData: null,
        isNewProject: false,
        catalogIds: [],
        productsMap: new Map(),
        allProductsLoaded: false,
        hasRestored: false,
        activeWorkflow: "none" as WorkflowType,
        targetSide: "front" as const,
        error: null,
        errorStage: null
      };
    }),

    processUrlParams: assign(({ context }) => {
      const flags = context.urlFlags?.transientData;
      const catalogIds: string[] = [];

      // Determine catalog IDs to load
      if (flags?.catalogNumber) {
        catalogIds.push(flags.catalogNumber);
      }

      // Determine target side for modals
      const targetSide = (flags?.side === "back" ? "back" : "front") as "front" | "back";

      return {
        ...context,
        catalogIds,
        targetSide
      };
    }),

    storeProjectData: assign(({ event, context }) => {
      assertEvent(event, "PROJECT_LOADED");

      // Extract catalog IDs from project quantities
      const catalogIds = new Set(context.catalogIds);
      if (event.data.quantities) {
        Object.keys(event.data.quantities).forEach(id => {
          catalogIds.add(id);
        });
      }

      // Special handling for URL params: merge strategy
      const urlCatalog = context.urlFlags?.transientData?.catalogNumber;
      if (urlCatalog && !catalogIds.has(urlCatalog)) {
        catalogIds.add(urlCatalog);
      }

      // If no catalog IDs found and no quantities, default to G500 for new projects
      if (catalogIds.size === 0 && !event.data.quantities) {
        catalogIds.add("G500");
      }

      return {
        ...context,
        projectData: event.data,
        projectUuid: event.data.uuid,
        catalogIds: Array.from(catalogIds)
      };
    }),

    markAsNewProject: assign(({ context }) => ({
      ...context,
      isNewProject: true
    })),

    storeCreatedProject: assign(({ event, context }) => {
      assertEvent(event, "PROJECT_CREATED");
      return {
        ...context,
        projectUuid: event.uuid,
        isNewProject: false
      };
    }),

    storeProducts: assign(({ event, context }) => {
      assertEvent(event, "PRODUCTS_LOADED");
      return {
        ...context,
        productsMap: event.productsMap,
        allProductsLoaded: true
      };
    }),

    markDataRestored: assign(({ context }) => ({
      ...context,
      hasRestored: true
    })),

    determineWorkflow: assign(({ context }) => {
      let activeWorkflow: WorkflowType = "none";
      const { urlFlags } = context;

      if (!urlFlags) {
        return {
          ...context,
          activeWorkflow
        };
      }
      const { workflowFlags, transientData } = urlFlags;

      // Determine target side from URL params (defaults to "front")
      const targetSide = (transientData?.side as "front" | "back") || "front";
      if (workflowFlags.hasDesignLibrary) {
        activeWorkflow = "designLibrary";
      } else if (workflowFlags.hasCanva) {
        activeWorkflow = "canva";
      } else if (workflowFlags.hasAiDesign) {
        activeWorkflow = "aiDesign";
      } else if (transientData.artworkUrls || transientData.uploadUrls) {
        activeWorkflow = "autoUpload";
      } else if (
        transientData.catalogNumber &&
        transientData.pdpQuantities &&
        transientData.pdpSizeData
      ) {
        activeWorkflow = "pdpOnly";
      }

      return {
        ...context,
        activeWorkflow,
        targetSide
      };
    }),

    storeError: assign(({ event, context }, params?: { stage: InitStage }) => {
      const error = "error" in event ? event.error : new Error("Unknown error");
      return {
        ...context,
        error,
        errorStage: params?.stage || null
      };
    }),

    clearError: assign(({ context }) => ({
      ...context,
      error: null,
      errorStage: null
    })),

    resetContext: assign(() => ({
      urlFlags: null,
      projectUuid: null,
      projectData: null,
      isNewProject: false,
      catalogIds: [],
      productsMap: new Map(),
      allProductsLoaded: false,
      hasRestored: false,
      activeWorkflow: "none" as WorkflowType,
      targetSide: "front" as const,
      error: null,
      errorStage: null,
      isEditMode: false,
      isSample: false
    }))
  }
}).createMachine({
  id: "designLabInit",
  context: () => ({
    urlFlags: null,
    projectUuid: null,
    projectData: null,
    isNewProject: false,
    catalogIds: [],
    productsMap: new Map(),
    allProductsLoaded: false,
    hasRestored: false,
    activeWorkflow: "none",
    targetSide: "front",
    error: null,
    errorStage: null,
    isEditMode: false,
    isSample: false
  }),
  initial: "idle",
  states: {
    idle: {
      on: {
        START_INIT: {
          target: "readingParams",
          actions: "setInitialContext"
        }
      }
    },

    readingParams: {
      entry: ["processUrlParams", "determineWorkflow"], // Determine workflow right at the start!
      always: {
        target: "loadingProject"
      }
    },

    loadingProject: {
      on: {
        PROJECT_LOADED: {
          target: "loadingProducts",
          actions: "storeProjectData"
        },
        PROJECT_NOT_FOUND: [
          {
            target: "creatingProject",
            guard: "shouldCreateProject"
          },
          {
            target: "loadingProducts",
            actions: "markAsNewProject"
          }
        ],
        PROJECT_ERROR: {
          target: "errorRecovery",
          actions: ["storeError", { type: "storeError", params: { stage: "loadingProject" } }]
        }
      }
    },

    creatingProject: {
      on: {
        PROJECT_CREATED: {
          target: "loadingProducts",
          actions: "storeCreatedProject"
        },
        PROJECT_ERROR: {
          target: "errorRecovery",
          actions: ["storeError", { type: "storeError", params: { stage: "creatingProject" } }]
        }
      }
    },

    loadingProducts: {
      on: {
        PRODUCTS_LOADED: {
          target: "restoringData",
          actions: "storeProducts"
        },
        PRODUCTS_ERROR: {
          target: "errorRecovery",
          actions: ["storeError", { type: "storeError", params: { stage: "loadingProducts" } }]
        }
      }
    },

    restoringData: {
      on: {
        DATA_RESTORED: {
          target: "processingWorkflows",
          actions: "markDataRestored"
        },
        RESTORATION_ERROR: {
          target: "errorRecovery",
          actions: ["storeError", { type: "storeError", params: { stage: "restoringData" } }]
        }
      }
    },

    processingWorkflows: {
      initial: "checkingWorkflow",
      states: {
        checkingWorkflow: {
          always: [
            { target: "autoUpload", guard: "hasAutoUpload" },
            { target: "designLibrary", guard: "hasDesignLibrary" },
            { target: "canva", guard: "hasCanva" },
            { target: "aiDesign", guard: "hasAiDesign" },
            { target: "pdpOnly", guard: "hasPdpOnly" },
            { target: "complete" }
          ]
        },

        autoUpload: {
          on: {
            UPLOAD_COMPLETE: { target: "complete" },
            WORKFLOW_ERROR: {
              target: "complete",
              actions: [
                "storeError",
                { type: "storeError", params: { stage: "processingWorkflows" } }
              ]
            }
          }
        },

        designLibrary: {
          // Modal workflow - transitions to complete when workflow processor calls WORKFLOW_COMPLETE
          on: {
            WORKFLOW_COMPLETE: { target: "complete" },
            WORKFLOW_ERROR: {
              target: "complete",
              actions: [
                "storeError",
                { type: "storeError", params: { stage: "processingWorkflows" } }
              ]
            }
          }
        },

        canva: {
          // Modal workflow - transitions to complete when workflow processor calls WORKFLOW_COMPLETE
          on: {
            WORKFLOW_COMPLETE: { target: "complete" },
            WORKFLOW_ERROR: {
              target: "complete",
              actions: [
                "storeError",
                { type: "storeError", params: { stage: "processingWorkflows" } }
              ]
            }
          }
        },

        aiDesign: {
          // Modal workflow - transitions to complete when workflow processor calls WORKFLOW_COMPLETE
          on: {
            WORKFLOW_COMPLETE: { target: "complete" },
            WORKFLOW_ERROR: {
              target: "complete",
              actions: [
                "storeError",
                { type: "storeError", params: { stage: "processingWorkflows" } }
              ]
            }
          }
        },

        pdpOnly: {
          // Process PDP quantities without modal
          on: {
            WORKFLOW_COMPLETE: { target: "complete" }
          }
        },

        complete: {
          type: "final"
        }
      },
      onDone: {
        target: "ready"
      }
    },

    errorRecovery: {
      // Graceful degradation - use project data as fallback
      always: [
        {
          // If we have project data, continue with what we have
          target: "ready",
          guard: ({ context }) => Boolean(context.projectData)
        },
        {
          // Otherwise go to error state
          target: "error"
        }
      ]
    },

    ready: {
      type: "final",
      entry: ["clearError"]
    },

    error: {
      type: "final",
      on: {
        RETRY_INIT: { target: "readingParams" },
        RESET: {
          target: "idle",
          actions: "resetContext"
        }
      }
    }
  }
});

// Type-safe event creators
export const designLabInitEvents = {
  startInit: (urlFlags: UrlFlagsContext, isEditMode: boolean, isSample: boolean) => ({
    type: "START_INIT" as const,
    urlFlags,
    isEditMode,
    isSample
  }),

  projectLoaded: (data: models.designLab.getLatestDesignProject.Response) => ({
    type: "PROJECT_LOADED" as const,
    data
  }),

  projectNotFound: () => ({ type: "PROJECT_NOT_FOUND" as const }),

  projectError: (error: Error) => ({ type: "PROJECT_ERROR" as const, error }),

  projectCreated: (uuid: string) => ({ type: "PROJECT_CREATED" as const, uuid }),

  productsLoaded: (productsMap: Map<string, printOnDemand.getProduct.Composition>) => ({
    type: "PRODUCTS_LOADED" as const,
    productsMap
  }),

  productsError: (error: Error) => ({ type: "PRODUCTS_ERROR" as const, error }),

  dataRestored: () => ({ type: "DATA_RESTORED" as const }),

  restorationError: (error: Error) => ({ type: "RESTORATION_ERROR" as const, error }),

  workflowComplete: () => ({ type: "WORKFLOW_COMPLETE" as const }),

  workflowError: (error: Error) => ({ type: "WORKFLOW_ERROR" as const, error }),

  librarySelection: (artworkId: number) => ({ type: "LIBRARY_SELECTION" as const, artworkId }),

  libraryCancelled: () => ({ type: "LIBRARY_CANCELLED" as const }),

  canvaComplete: (file: File) => ({ type: "CANVA_COMPLETE" as const, file }),

  canvaCancelled: () => ({ type: "CANVA_CANCELLED" as const }),

  uploadComplete: () => ({ type: "UPLOAD_COMPLETE" as const }),

  aiComplete: () => ({ type: "AI_COMPLETE" as const }),

  retryInit: () => ({ type: "RETRY_INIT" as const }),

  reset: () => ({ type: "RESET" as const })
};
